import * as React from "react";
import styled from "styled-components";
import {useRef, useState} from "react";
import {observer} from "mobx-react";
import type {Field, IFieldAdapter} from "../../../../data/models/field/Field";
import {isDefaultField} from "../../../../data/models/field/FieldUtils";
import type {IModel} from "../../../../data/models/Model";
import {FieldDataType, Permission, XyiconFeature} from "../../../../generated/api/base";
import {TimeUtils} from "../../../../utils/TimeUtils";
import {ReactUtils} from "../../../utils/ReactUtils";
import {baseDistance, ELLIPSIS, FLEXCENTER, radius} from "../../styles/styles";
import DOLFieldIcon from "../../icons/inherit-link-new.svg?react";
import {useAppStore} from "../../../../StateManager";
import {FieldInputV5} from "../../details/datatypes/FieldInputV5";
import {PropagatedFieldInDetailsContent} from "../../details/datatypes/SingleFieldInput.styled";
import {isNameOrModelFieldEditable, isTypeFieldEditable} from "../../../modules/abstract/sidepanel/tabs/details/field/FieldInputUtils";
import {LookupUtils} from "../../../modules/abstract/sidepanel/tabs/details/LookupUtils";
import {MultiSelectInputV5} from "../../details/datatypes/multi/MultiSelectInputV5";
import {MultiSelectInputStyled} from "../../details/datatypes/multi/MultiSelect.styled";
import {colorPalette} from "../../styles/colorPalette";
import type {ILookupFieldOption} from "../../../modules/abstract/sidepanel/tabs/details/DetailsContent";
import type {IError} from "../../../../data/TransportLayer";
import type {IFieldPropagation} from "../../../../data/state/AppActions";
import {SelectInputStyled} from "../../input/select/SelectInputV5";

interface ITableCellProps {
	readonly width: number;
	readonly field: IFieldAdapter;
	readonly fieldValue: any;
	readonly item: IModel;
	readonly columnIndex?: number;
	readonly isSimpleTable: boolean;
	readonly hasMultipleFocusedItems: boolean;
	readonly getItemsToUpdate: () => IModel[];
	readonly scrollTableToShowFullCell: (e: React.MouseEvent) => void;
}

export const TableCellV5 = observer((props: ITableCellProps) => {
	const {width, field, item, columnIndex, isSimpleTable, fieldValue, hasMultipleFocusedItems, getItemsToUpdate, scrollTableToShowFullCell} = props;

	const _fieldInput = useRef<FieldInputV5>(null);
	const _parentRefArray = useRef([]);
	const appState = useAppStore((state) => state.appState);
	const [requestedLookupOptions, setRequestedLookupOptions] = useState<{item: IModel; field: IFieldAdapter} | null>(null);

	const isUpdating = (): boolean => {
		if (!item || !field || !appState) {
			return false;
		}

		return appState.itemFieldUpdateManager.isItemFieldBeingUpdated(item.id, field.refId, item.ownFeature);
	};

	const onChange = (value: string) => {
		const itemsToCheckPermission = getItemsToUpdate();
		const itemsToUpdate = getItemsWithPermission(itemsToCheckPermission);
		const requestPropName = isDefaultField(field) ? field.getterName : field.refId;

		TimeUtils.waitUpdate(appState.actions.updateFields(itemsToUpdate, {[requestPropName]: value}), appState.app.notificationContainer);
	};

	const onClickCell = (event: React.MouseEvent) => {
		scrollTableToShowFullCell(event);
		if (isCellEditable()) {
			event.stopPropagation();
		}
	};

	const onDoubleClick = (e: React.MouseEvent) => {
		if (isCellEditable()) {
			e.stopPropagation();
		}
	};

	const getItemsWithPermission = (itemsToCheck: IModel[]) => {
		return itemsToCheck.filter((item) => appState.actions.getFieldPermission(field, [item]) > Permission.View);
	};

	const isCellEditable = () => {
		const {actions, user} = appState;

		if (field && item && user) {
			// We don't support updating multiple fields that have validation
			// Simply because the the first validation to be implemented is the "UniqueGIAI" validation
			// Which means all values must be unique. So if we're about to update more than one of them with the same value
			// It would lead to problems, or at least invalid values.
			// If we say boolean fields are not editable, then the little toggler is not visible, meaning it breaks the animation of the toggleswitch
			// as soon you click it, because it becomes non editable while we're waiting for the response from the backend.
			// So for boolean fields, and multiplechoicelist, we're making an exception
			if (
				(field.hasValidation && hasMultipleFocusedItems) ||
				(isUpdating() &&
					![
						FieldDataType.SingleChoiceList,
						FieldDataType.MultipleChoiceList,
						FieldDataType.Boolean,
						FieldDataType.PeopleSingleChoiceList,
						FieldDataType.PeopleMultipleChoiceList,
					].includes(field.dataType))
			) {
				return false;
			}
			// 'isAssigned' will be undefined for system/readonly fields (id, type, lastModifiedBy, lastModifiedAt, model, name)
			const isAssigned = actions.getFieldsForType(item.typeId, field.feature).find((f) => f.id === (field as Field).id)?.value;
			const hasPermission = actions.getFieldPermission(field, [item]) > Permission.View;
			const isInherited = field.displayOnLinks && field.feature !== item.ownFeature && !Object.keys(item.fieldData).includes(field.refId);
			const isHidden = appState.actions.isFieldHiddenByMasking(item, field);
			const isCatalogModel = field.refId.includes("model") && item.ownFeature !== XyiconFeature.Xyicon;
			const isCatalogModelEditable = user?.getOrganizationPermission(XyiconFeature.XyiconCatalog) > Permission.View;
			const isNameEditable = isNameOrModelFieldEditable(item, field, appState);
			const isTypeOrModelEditable = isTypeFieldEditable(item, field, appState);

			return (
				editableFieldTypes().includes(field.dataType) &&
				!field.hasFormula &&
				!isInherited &&
				!isHidden &&
				((isAssigned && hasPermission) || isTypeOrModelEditable || (isCatalogModel && isCatalogModelEditable) || isNameEditable)
			);
		}
		return false;
	};

	const editableFieldTypes = () => {
		return [
			FieldDataType.Type,
			FieldDataType.SingleLineText,
			FieldDataType.MultiLineText,
			FieldDataType.Numeric,
			FieldDataType.SingleChoiceList,
			FieldDataType.Boolean,
			FieldDataType.DateTime,
			FieldDataType.MultipleChoiceList,
			FieldDataType.PeopleSingleChoiceList,
			FieldDataType.PeopleMultipleChoiceList,
			FieldDataType.Email,
			FieldDataType.IPAddress,
		];
	};

	const isEditable = isCellEditable();
	const value = field ? appState.actions.getOwnFieldValue(item, field.refId) : fieldValue;
	const propagations: IFieldPropagation[] = field ? appState.actions.getFieldPropagations(item, field) : [];
	// Show input if feature is the same and type mapping matches or default
	// Note is there a way showInput will become false here?
	// Note for the above question: if the Input field is an inherited DOL field
	const shouldDisplayDOLFieldIcon = fieldValue && field?.displayOnLinks && propagations.length > 0;
	const showOwnInput = !appState.actions.isFieldHiddenByMasking(item, field);
	const isXyiconModel = field?.refId.includes("model") && item?.ownFeature === XyiconFeature.Xyicon;

	// DoL placeholder removal: hide the own-value placeholder when field is non-editable and has only inherited (DoL) value
	const hasOwnValue = value != null && value !== "";
	const shouldHidePlaceholder = propagations.length > 0 && !isEditable && !hasOwnValue;
	const isMultiLineField = [FieldDataType.MultiLineText, FieldDataType.MultipleChoiceList, FieldDataType.PeopleMultipleChoiceList].includes(
		field?.dataType,
	);

	const isLookup = field && LookupUtils.isLookupField(field, item, appState.actions);
	let lookupFieldOptions: ILookupFieldOption[];
	let selectedLookupFieldOptions: ILookupFieldOption[];
	let onLookupChange: (selectedOptions: ILookupFieldOption[]) => Promise<{result: any; error: IError}>;

	if (isLookup) {
		if (requestedLookupOptions?.item === item && requestedLookupOptions.field === field) {
			lookupFieldOptions = LookupUtils.getLookupFieldOptions(appState, field, item);
			selectedLookupFieldOptions = LookupUtils.getSelectedLookupFieldOptions(appState, lookupFieldOptions, item);
		} else {
			lookupFieldOptions = [];
			selectedLookupFieldOptions = LookupUtils.getSelectedLookupFieldOptionsForField(appState, field, item);
		}
		const previousSelectedIds = new Set(selectedLookupFieldOptions.map((option) => option.xyicon.id));
		onLookupChange = (selectedOptions) => {
			// A pill can be removed before opening the menu. Resolve current eligible
			// options and links at save time, applying only this edit's selection delta
			// so intervening link changes are preserved.
			const currentOptions = LookupUtils.getLookupFieldOptions(appState, field, item);
			const requestedIds = new Set(selectedOptions.map((option) => option.xyicon.id));
			const currentSelectedIds = new Set(
				LookupUtils.getSelectedLookupFieldOptions(appState, currentOptions, item)
					.filter((option) => !previousSelectedIds.has(option.xyicon.id) || requestedIds.has(option.xyicon.id))
					.map((option) => option.xyicon.id),
			);
			for (const id of requestedIds) {
				if (!previousSelectedIds.has(id)) {
					currentSelectedIds.add(id);
				}
			}
			const nextSelected = currentOptions.filter((option) => currentSelectedIds.has(option.xyicon.id));
			return LookupUtils.getOnChangeForLookup(appState.app.transport, appState, currentOptions, item)(nextSelected);
		};
	}

	return (
		<TableCellStyled
			className={ReactUtils.cls("td TableCell", {
				italic: item.ownFeature === XyiconFeature.User,
				editable: isEditable,
				date: field?.dataType === FieldDataType.DateTime,
				emptySingleSelectOrDateTime: [FieldDataType.SingleChoiceList, FieldDataType.DateTime].includes(field?.dataType),
				textAlignRight: [FieldDataType.Numeric].includes(field?.dataType),
				multiline: field?.dataType === FieldDataType.MultiLineText,
				unassigned: !appState.actions.getFieldsForType(item?.typeId, field?.feature).find((f) => f.id === (field as Field).id)?.value,
			})}
			data-table-column-index={columnIndex}
			style={{width, minWidth: width}}
			onDoubleClick={onDoubleClick}
			onClick={onClickCell}
		>
			{isSimpleTable ? (
				<span className="simple">{fieldValue}</span>
			) : isLookup ? (
				<MultiSelectInputV5
					key={field.refId}
					options={lookupFieldOptions}
					selected={selectedLookupFieldOptions}
					onClick={() => setRequestedLookupOptions((previous) => (previous?.item === item && previous.field === field ? previous : {item, field}))}
					renderOptionWhenClosed={(obj) => (
						<div className="hbox alignCenter">
							{obj.value}&nbsp;<span style={{color: "#A0A0A0"}}>({obj.xyicon.refId})</span>
						</div>
					)}
					onChange={onLookupChange}
					focused={false}
					disabled={false}
				/>
			) : (
				<>
					{showOwnInput && !shouldHidePlaceholder && (
						<FieldInputV5
							field={field}
							item={item}
							ref={_fieldInput}
							dataType={isXyiconModel ? FieldDataType.Type : field.dataType}
							dataTypeSettings={field.dataTypeSettings}
							value={value}
							onChange={onChange}
							onClick={onClickCell}
							disabled={!isEditable}
							isUpdating={isUpdating()}
							dropdownIcon={false}
							inline={true}
							isInherited={false}
							getItemsToUpdate={getItemsToUpdate}
						/>
					)}
					{propagations?.map((propagation: IFieldPropagation, index: number) => (
						<PropagatedFieldInDetailsContent
							key={`${field.fieldID || field.refId} ${index}`}
							ref={_parentRefArray.current[index]}
							$onlyFieldValue={(!showOwnInput || shouldHidePlaceholder) && index === 0}
							className="inline"
						>
							{shouldDisplayDOLFieldIcon && (
								<DOLBadgeStyled className={isMultiLineField ? "multiLine" : ""}>
									<DOLFieldIcon />
								</DOLBadgeStyled>
							)}
							<FieldInputV5
								field={appState.actions.getFieldById(field.fieldID) || field}
								dataType={field.dataType}
								dataTypeSettings={field.dataTypeSettings}
								value={propagation.value}
								disabled={true}
								dropdownIcon={false}
								inline={true}
								isInherited={true}
							/>
						</PropagatedFieldInDetailsContent>
					))}
				</>
			)}
		</TableCellStyled>
	);
});

const DOLBadgeStyled = styled.div`
	position: relative;
	z-index: 1;
	display: ${FLEXCENTER};
	padding-left: ${baseDistance.sm};

	svg {
		width: ${baseDistance.md};
		height: ${baseDistance.md};
		color: #70a787;
	}

	&.multiLine {
		align-self: flex-start;
		padding-top: ${baseDistance.sm};
	}
`;

export const TableCellStyled = styled.div`
	border-radius: ${radius.xs};
	align-content: center;
	min-height: ${baseDistance.lg};

	${PropagatedFieldInDetailsContent} {
		display: flex;
		align-items: center;
		padding: 0;

		${SelectInputStyled} {
			padding-left: ${baseDistance.sm};
		}

		.MultiLineLabel .content {
			overflow: hidden;
		}
	}

	.catalog-thumbnail {
		position: relative;

		svg {
			position: absolute;
			top: 0;
			right: 0;
			width: 22px;
			height: 28px;
		}
	}

	&.editable {
		&.boolean {
			min-height: 40px;
			${FLEXCENTER};
			flex-direction: column;
		}

		:focus-visible {
			outline: none;
		}
	}

	.vbox {
		display: flex;
		flex-direction: column;
		align-items: flex-start;

		.TableCellValue {
			width: -webkit-fill-available;
		}
	}

	&.editable.boolean {
		height: auto;
		display: block;
	}

	span.simple {
		display: block;
		width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: pre-line;
	}

	&.unassigned .nullLabel {
		visibility: hidden;
	}

	> ${MultiSelectInputStyled} {
		border: 2px solid transparent;
		min-height: 40px;
		border-radius: ${radius.xs};
		${ELLIPSIS};

		&:hover {
			background-color: ${colorPalette.gray.c200Light};
			border-color: ${colorPalette.primary.c500Primary};
		}

		&:focus {
			background-color: ${colorPalette.white};
			border: 2px solid ${colorPalette.primary.c500Primary};

			&::selection {
				background-color: #fbb700;
				color: ${colorPalette.white};
			}
		}

		&.isActive {
			background-color: ${colorPalette.white};
			border: 2px solid ${colorPalette.primary.c500Primary};
		}
	}
`;
